/**
 * MatchingPanel — Tab „Abgleich" (architecture-bank-reconciliation.md §10.1).
 *
 * Führt Parser-Ergebnis (BR-3), Matching-Engine (BR-4/BR-5) und Persistenz
 * (BR-6 `bankApi`-Confirm-Flows) zusammen. Bereiche:
 *   - „Sichere Treffer" (confidence `auto`) mit Bulk-Confirm; je Treffer mit
 *     Mahnstopp-Hinweis „⚠ stoppt Mahnstufe n …" bei `hasActiveDunning` (§8.4/E6).
 *   - „Zu prüfen" (confidence `suggest`) als Karten mit Reason-Chips,
 *     Bestätigen / Ignorieren / (Eingang) manuellem Split.
 *   - Umbuchungs-Bulk-Aktion (beide Seiten in EINEM Batch, §8.6).
 *   - Rücklastschrift-Karten (`confirmPaymentReversal`, §8.9).
 *
 * Der verbindliche Aufrufer-Vorfilter (§9.2) liegt in `buildReconcileSuggestions`
 * (interne Umbuchungen + Rücklastschriften werden VOR den Engines herausgefiltert).
 *
 * @module MatchingPanel
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import { useAuth } from '../../context/AuthContext';
import { centsToEuros } from '../../utils/moneyUtils';
import {
  confirmIncomingMatch,
  confirmExpenseMatch,
  ignoreTransaction,
  ignoreTransactionsBatch,
  confirmPaymentReversal,
} from '../../api/bankApi';
import { BankTransaction, BankTxIgnoreReason } from '../../types/bankTypes';
import {
  MatchSuggestion,
  ReversalSuggestion,
  MatchableInvoice,
  MatchableExpense,
  selectUnsuggestedTransactions,
  getDisplayCounterparty,
} from '../../utils/bankMatching';
import logger from '../../utils/logger';
import { loadReconcileData, ReconcileData } from './loadReconcileData';
import { buildReconcileSuggestions, ReconcileSuggestionsResult } from './reconcileSuggestions';
import SplitMatchModal from './SplitMatchModal';

export interface MatchingPanelProps {
  /** Bump to force a reload (e.g. after a successful import in another tab). */
  refreshToken: number;
  /**
   * BR-18-A (§8.11): optionaler Cross-Link „Ausgaben ohne Beleg →" aus der
   * „Ohne Vorschlag"-Sektion in den Tab „Ohne Beleg" (Tab-Wechsel ist
   * Parent-State, kein eigener Route — daher Callback statt Link).
   */
  onNavigateWithoutReceipt?: () => void;
}

const IGNORE_REASONS: BankTxIgnoreReason[] = [
  'internal_transfer',
  'private',
  'tax_payment',
  'payroll',
  'bank_fee',
  'other',
];

function getTodayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const MatchingPanel: React.FC<MatchingPanelProps> = ({ refreshToken, onNavigateWithoutReceipt }) => {
  const { t, i18n } = useTranslation('bank');
  const { effectiveScopeUid } = useAuth();

  const [data, setData] = useState<ReconcileData | null>(null);
  const [suggestions, setSuggestions] = useState<ReconcileSuggestionsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [splitTx, setSplitTx] = useState<BankTransaction | null>(null);
  // BR-18-A (§8.11): per-Zeile gewählter Zuordnungs-Kandidat (invoiceId bzw.
  // expenseId) in der „Ohne Vorschlag"-Sektion, keyed by txId.
  const [unsuggestedSelection, setUnsuggestedSelection] = useState<Record<string, string>>({});

  const locale = i18n.language || 'de-DE';

  const formatAmount = useCallback(
    (cents: number) =>
      centsToEuros(cents).toLocaleString(locale, { style: 'currency', currency: 'EUR' }),
    [locale],
  );

  const load = useCallback(async () => {
    if (!effectiveScopeUid) return;
    setLoading(true);
    setError(null);
    setActionError(null);
    try {
      const d = await loadReconcileData(effectiveScopeUid);
      const s = buildReconcileSuggestions({
        unmatchedTxs: d.unmatchedTxs,
        allTxs: d.allTxs,
        matchedCreditTxs: d.matchedCreditTxs,
        invoices: d.invoices,
        expenses: d.expenses,
        accounts: d.accounts,
        todayISO: getTodayISO(),
      });
      setData(d);
      setSuggestions(s);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.warn('MatchingPanel.load failed', msg);
      setError(msg || t('matching.loadFailed', 'Abgleich-Daten konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [effectiveScopeUid, t]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  // ── Derived suggestion groups ───────────────────────────────────────────────

  const { autoSuggestions, reviewSuggestions } = useMemo(() => {
    const auto: MatchSuggestion[] = [];
    const review: MatchSuggestion[] = [];
    if (suggestions) {
      for (const s of [...suggestions.incoming, ...suggestions.outgoing]) {
        if (s.confidence === 'auto') auto.push(s);
        else review.push(s);
      }
    }
    return { autoSuggestions: auto, reviewSuggestions: review };
  }, [suggestions]);

  const internalTransfers = suggestions?.internalTransfers ?? [];
  const reversals = suggestions?.reversals ?? [];
  const unmatchedTxs = data?.unmatchedTxs ?? [];

  // BR-18-A (§8.11): billigste verfügbare Quelle für „wurde je etwas
  // importiert" — bereits geladene Daten (`allTxs` inkl. `matched`/`ignored`-
  // Rand + `accounts[].lastImportAt`), KEINE zusätzliche Query.
  const hasAnyTransactions =
    (data?.allTxs.length ?? 0) > 0 || (data?.accounts.some((a) => a.lastImportAt != null) ?? false);

  // Vereinigte Ausschluss-Menge aus GENAU den vier Arrays, die auch die
  // bestehenden Sektionen speisen (§8.11) — garantiert komplementär, kein
  // Umsatz erscheint doppelt.
  const coveredTxIds = useMemo(() => {
    const ids = new Set<string>();
    for (const sug of autoSuggestions) ids.add(sug.txId);
    for (const sug of reviewSuggestions) ids.add(sug.txId);
    for (const pair of internalTransfers) {
      ids.add(pair.debitTxId);
      if (pair.creditTxId) ids.add(pair.creditTxId);
    }
    for (const rev of reversals) ids.add(rev.debitTxId);
    return ids;
  }, [autoSuggestions, reviewSuggestions, internalTransfers, reversals]);

  const unsuggestedTxs = useMemo(
    () => selectUnsuggestedTransactions(unmatchedTxs, coveredTxIds),
    [unmatchedTxs, coveredTxIds],
  );

  // ── Action handlers ─────────────────────────────────────────────────────────

  const runAction = useCallback(
    async (fn: () => Promise<void>) => {
      setBusy(true);
      setActionError(null);
      try {
        await fn();
        await load();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        logger.warn('MatchingPanel action failed', msg);
        setActionError(msg || t('matching.actionFailed', 'Aktion fehlgeschlagen.'));
      } finally {
        // Reviewer 🟡2: busy muss nach ERFOLG genauso zurückgesetzt werden wie
        // nach einem Fehler — sonst bleiben nach der ersten erfolgreichen
        // Aktion alle Buttons dauerhaft disabled (bis Page-Reload). `finally`
        // läuft erst NACH `await load()`, also kein Race mit dem Reload.
        setBusy(false);
      }
    },
    [load, t],
  );

  const confirmSuggestion = useCallback(
    (sugg: MatchSuggestion): Promise<void> => {
      if (!data) return Promise.resolve();
      const tx = data.txById.get(sugg.txId);
      if (!tx) return Promise.reject(new Error(`tx ${sugg.txId} nicht geladen.`));
      const matchType = sugg.confidence === 'auto' ? 'auto' : 'manual';

      if (sugg.kind === 'expense') {
        return confirmExpenseMatch({ tx, expenseIds: sugg.targetIds, matchType });
      }
      // invoice | split-invoices
      const parts =
        sugg.kind === 'split-invoices' && sugg.splitParts
          ? sugg.splitParts
          : [{ invoiceId: sugg.targetIds[0], amountCents: tx.amountCents }];
      return confirmIncomingMatch({ tx, parts, matchType });
    },
    [data],
  );

  const handleConfirm = (sugg: MatchSuggestion) => runAction(() => confirmSuggestion(sugg));

  const handleBulkConfirm = () =>
    runAction(async () => {
      // Each confirm is its own atomic batch (§9.3); iterate the safe hits.
      for (const s of autoSuggestions) {
        // eslint-disable-next-line no-await-in-loop -- sequential, each an atomic batch
        await confirmSuggestion(s);
      }
    });

  const handleIgnore = (txId: string, reason: BankTxIgnoreReason) =>
    runAction(() => ignoreTransaction(txId, reason));

  const handleMarkTransfers = () =>
    runAction(() => {
      const ids: string[] = [];
      for (const pair of suggestions?.internalTransfers ?? []) {
        ids.push(pair.debitTxId);
        if (pair.creditTxId) ids.push(pair.creditTxId);
      }
      return ignoreTransactionsBatch(ids, 'internal_transfer');
    });

  const handleConfirmReversal = (rev: ReversalSuggestion) =>
    runAction(() => {
      if (!data) return Promise.resolve();
      const debitTx = data.txById.get(rev.debitTxId);
      const originalTx = data.txById.get(rev.originalTxId);
      if (!debitTx || !originalTx) {
        return Promise.reject(new Error('Rücklastschrift-Umsatz nicht geladen.'));
      }
      const paymentsToReverse = rev.reversalOfPaymentIds
        .map((pid) => data.paymentById.get(pid))
        .filter((p): p is NonNullable<typeof p> => p != null);
      if (paymentsToReverse.length === 0) {
        return Promise.reject(new Error('Keine zu stornierenden Zahlungen gefunden.'));
      }
      return confirmPaymentReversal({ debitTx, originalTx, paymentsToReverse });
    });

  const handleSplitConfirm = (parts: Array<{ invoiceId: string; amountCents: number }>) => {
    if (!splitTx) return;
    const tx = splitTx;
    setSplitTx(null);
    runAction(() => confirmIncomingMatch({ tx, parts, matchType: 'manual' }));
  };

  /**
   * BR-18-A (§8.11) — manuelle Zuordnung einer Zeile aus der „Ohne
   * Vorschlag"-Sektion: Gutschrift → `confirmIncomingMatch` (1 Part, Betrag =
   * tx.amountCents), Belastung → `confirmExpenseMatch`. Reuse der
   * Bestands-Confirm-Flows (§9.3) — KEINE neue API.
   */
  const handleAssignUnsuggested = (tx: BankTransaction) => {
    const targetId = unsuggestedSelection[tx.id];
    if (!targetId) return;
    if (tx.direction === 'credit') {
      runAction(() =>
        confirmIncomingMatch({
          tx,
          parts: [{ invoiceId: targetId, amountCents: tx.amountCents }],
          matchType: 'manual',
        }),
      );
    } else {
      runAction(() => confirmExpenseMatch({ tx, expenseIds: [targetId], matchType: 'manual' }));
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  /** Mahnstopp-Hinweise (§8.4/E6) für eine Eingangs-Zuordnung. */
  const dunningHints = (sugg: MatchSuggestion): Array<{ level: number | null; number: string }> => {
    if (!data || (sugg.kind !== 'invoice' && sugg.kind !== 'split-invoices')) return [];
    return sugg.targetIds
      .map((id) => data.invoiceById.get(id))
      .filter((inv): inv is NonNullable<typeof inv> => inv != null && inv.hasActiveDunning)
      .map((inv) => ({ level: inv.dunningLevel, number: inv.invoiceNumber }));
  };

  const targetLabel = (sugg: MatchSuggestion): string => {
    if (!data) return sugg.targetIds.join(', ');
    if (sugg.kind === 'expense') {
      return sugg.targetIds
        .map((id) => data.expenseById.get(id)?.supplierName ?? id)
        .join(', ');
    }
    return sugg.targetIds
      .map((id) => data.invoiceById.get(id)?.invoiceNumber ?? id)
      .join(', ');
  };

  const renderMahnstopp = (sugg: MatchSuggestion) => {
    const hints = dunningHints(sugg);
    if (hints.length === 0) return null;
    return (
      <div className="mt-1.5 space-y-0.5">
        {hints.map((h) => (
          <p key={h.number} className="text-xs text-[var(--tone-warning-fg)]" data-testid="mahnstopp-hint">
            {t('matching.dunningStop', {
              level: h.level ?? '?',
              invoice: h.number,
              defaultValue: '⚠ stoppt Mahnstufe {{level}} zu {{invoice}}',
            })}
          </p>
        ))}
      </div>
    );
  };

  const renderReasonChips = (sugg: MatchSuggestion) => (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {sugg.reasons.map((r, idx) => (
        <span
          key={`${r.code}-${idx}`}
          title={r.detail}
          className="text-[10px] px-1.5 py-0.5 rounded bg-[rgb(var(--surface-inset-rgb)/0.7)] text-secondary border border-strong"
        >
          {t(`matching.reasons.${r.code}`, r.code)}
        </span>
      ))}
    </div>
  );

  const renderIgnoreMenu = (txId: string) => (
    <select
      aria-label={t('matching.ignoreAs', 'Ignorieren als')}
      defaultValue=""
      disabled={busy}
      onChange={(e) => {
        const val = e.target.value as BankTxIgnoreReason | '';
        if (val) handleIgnore(txId, val);
        e.target.value = '';
      }}
      className="text-xs bg-surface-raised border border-hairline rounded px-2 py-1 text-muted focus:outline-none focus:border-indigo-500"
    >
      <option value="" disabled>
        {t('matching.ignore', 'Ignorieren…')}
      </option>
      {IGNORE_REASONS.map((r) => (
        <option key={r} value={r}>
          {t(`matching.ignoreReasons.${r}`, r)}
        </option>
      ))}
    </select>
  );

  const renderSuggestionRow = (sugg: MatchSuggestion, isAuto: boolean) => {
    const tx = data?.txById.get(sugg.txId);
    if (!tx) return null;
    const canSplit = sugg.kind === 'invoice' || sugg.kind === 'split-invoices';
    return (
      <div
        key={`${sugg.txId}-${sugg.kind}-${sugg.targetIds.join('_')}`}
        className="flex flex-col md:flex-row md:items-center gap-3 bg-surface border border-hairline rounded-lg p-3"
      >
        {/* Left: transaction */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className={tx.amountCents < 0 ? 'text-[var(--tone-danger-fg)] font-medium' : 'text-[var(--tone-success-fg)] font-medium'}>
              {formatAmount(tx.amountCents)}
            </span>
            <span className="text-faint text-xs">{tx.bookingDate}</span>
          </div>
          <p className="text-xs text-muted truncate">{getDisplayCounterparty(tx)}</p>
          <p className="text-[11px] text-faint truncate">{tx.purpose || '—'}</p>
        </div>

        {/* Middle: target */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-secondary truncate">
            {sugg.kind === 'expense'
              ? t('matching.expenseTarget', 'Ausgabe: {{name}}', { name: targetLabel(sugg) })
              : sugg.kind === 'split-invoices'
              ? t('matching.splitTarget', 'Split: {{list}}', { list: targetLabel(sugg) })
              : t('matching.invoiceTarget', 'Rechnung {{name}}', { name: targetLabel(sugg) })}
            <span className="ml-2 text-[11px] text-faint">({sugg.score})</span>
          </p>
          {sugg.overpaymentCents != null && sugg.overpaymentCents > 0 && (
            <p className="text-xs text-[var(--tone-warning-fg)]">
              {t('matching.overpayment', '+{{amount}} Überzahlung', {
                amount: formatAmount(sugg.overpaymentCents),
              })}
            </p>
          )}
          {renderReasonChips(sugg)}
          {renderMahnstopp(sugg)}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2 shrink-0">
          {!isAuto && (
            <>
              {canSplit && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setSplitTx(tx)}
                  className="text-xs px-2.5 py-1 text-accent-ink hover:text-indigo-200 disabled:opacity-40"
                >
                  {t('matching.split', 'Splitten')}
                </button>
              )}
              {renderIgnoreMenu(tx.id)}
            </>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => handleConfirm(sugg)}
            className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 text-white rounded-lg whitespace-nowrap"
          >
            {t('matching.confirm', 'Bestätigen')}
          </button>
        </div>
      </div>
    );
  };

  /**
   * BR-18-A (§8.11) — eine Zeile der „Ohne Vorschlag / manuell zuordnen"-
   * Sektion: Umsatz links, Ziel-Picker (Rechnung bei Gutschrift, Ausgabe bei
   * Belastung) + „Zuordnen" + Ignorieren-Menü rechts (reuse `renderIgnoreMenu`).
   */
  const renderUnsuggestedRow = (tx: BankTransaction) => {
    const invoices: MatchableInvoice[] = data?.invoices ?? [];
    const expenses: MatchableExpense[] = data?.expenses ?? [];
    const isCredit = tx.direction === 'credit';
    const selectedId = unsuggestedSelection[tx.id] ?? '';

    return (
      <div
        key={tx.id}
        className="flex flex-col md:flex-row md:items-center gap-3 bg-surface border border-hairline rounded-lg p-3"
      >
        {/* Left: transaction */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className={tx.amountCents < 0 ? 'text-[var(--tone-danger-fg)] font-medium' : 'text-[var(--tone-success-fg)] font-medium'}>
              {formatAmount(tx.amountCents)}
            </span>
            <span className="text-faint text-xs">{tx.bookingDate}</span>
          </div>
          <p className="text-xs text-muted truncate">{getDisplayCounterparty(tx)}</p>
          <p className="text-[11px] text-faint truncate">{tx.purpose || '—'}</p>
        </div>

        {/* Middle: target picker */}
        <div className="flex-1 min-w-0">
          <select
            aria-label={
              isCredit
                ? t('matching.unsuggested.selectInvoice', 'Rechnung wählen')
                : t('matching.unsuggested.selectExpense', 'Ausgabe wählen')
            }
            disabled={busy}
            value={selectedId}
            onChange={(e) =>
              setUnsuggestedSelection((prev) => ({ ...prev, [tx.id]: e.target.value }))
            }
            className="w-full text-xs bg-surface-raised border border-hairline rounded px-2 py-1.5 text-secondary focus:outline-none focus:border-indigo-500"
          >
            <option value="">
              {isCredit
                ? t('matching.unsuggested.selectInvoice', 'Rechnung wählen')
                : t('matching.unsuggested.selectExpense', 'Ausgabe wählen')}
            </option>
            {isCredit
              ? invoices.map((inv) => (
                  <option key={inv.invoiceId} value={inv.invoiceId}>
                    {inv.invoiceNumber} — {inv.buyerName}
                  </option>
                ))
              : expenses.map((exp) => (
                  <option key={exp.expenseId} value={exp.expenseId}>
                    {exp.supplierName}
                    {exp.invoiceNumber ? ` (${exp.invoiceNumber})` : ''}
                  </option>
                ))}
          </select>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2 shrink-0">
          {renderIgnoreMenu(tx.id)}
          <button
            type="button"
            disabled={busy || !selectedId}
            onClick={() => handleAssignUnsuggested(tx)}
            className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 disabled:opacity-50 text-white rounded-lg whitespace-nowrap"
          >
            {t('matching.action.assign', 'Zuordnen')}
          </button>
        </div>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <ClipLoader color="#6366f1" size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-4 text-[var(--tone-danger-fg)]">
        <p className="font-medium">{t('matching.loadFailed', 'Abgleich-Daten konnten nicht geladen werden.')}</p>
        <p className="text-sm mt-1">{error}</p>
        <button onClick={load} className="mt-3 text-sm underline hover:text-red-200">
          {t('matching.retry', 'Erneut versuchen')}
        </button>
      </div>
    );
  }

  // BR-18-A (§8.11): dreistufige Leer-/Zustands-Logik ersetzt die eine
  // irreführende „Keine offenen Umsätze"-Meldung — siehe DoD-Tabelle.
  const showNothingImported = !hasAnyTransactions;
  const showAllAssigned = hasAnyTransactions && unmatchedTxs.length === 0;

  return (
    <div className="space-y-6">
      {actionError && (
        <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">
          {actionError}
        </div>
      )}

      {/* BR-18-A (§8.11): dreistufige Leer-/Zustands-Logik — „nichts
          importiert" vs. „alles zugeordnet" vs. „importiert, keine Auto-
          Treffer" (dritter Fall zeigt direkt die „Ohne Vorschlag"-Sektion
          unten, KEINE Meldung hier). */}
      {showNothingImported && (
        <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center text-muted">
          {t('matching.empty.nothingImported', 'Noch keine Umsätze importiert. Importiere zuerst einen Kontoauszug.')}
        </div>
      )}

      {showAllAssigned && (
        <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center text-muted">
          {t('matching.empty.allAssigned', 'Alle importierten Umsätze sind zugeordnet.')}
        </div>
      )}

      {/* Rücklastschriften (§8.9) */}
      {reversals.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-[var(--tone-warning-fg)] mb-2">
            {t('matching.reversalsTitle', 'Rücklastschriften ({{count}})', { count: reversals.length })}
          </h3>
          <div className="space-y-2">
            {reversals.map((rev) => {
              const debitTx = data?.txById.get(rev.debitTxId);
              const originalTx = data?.txById.get(rev.originalTxId);
              if (!debitTx) return null;
              return (
                <div
                  key={rev.debitTxId}
                  className="flex flex-col md:flex-row md:items-center gap-3 bg-surface border border-amber-700/50 rounded-lg p-3"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-[var(--tone-danger-fg)] font-medium text-sm">{formatAmount(debitTx.amountCents)}</span>
                    <span className="ml-2 text-faint text-xs">{debitTx.bookingDate}</span>
                    <p className="text-xs text-muted">
                      {t('matching.reversalHint', 'Zahlung wurde zurückgebucht → Storno buchen')}
                    </p>
                    {originalTx && (
                      <p className="text-[11px] text-faint">
                        {t('matching.reversalOriginal', 'Ursprüngliche Gutschrift {{amount}} vom {{date}}', {
                          amount: formatAmount(originalTx.amountCents),
                          date: originalTx.bookingDate,
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleConfirmReversal(rev)}
                    className="text-xs px-3 py-1.5 bg-amber-700 hover:bg-amber-600 disabled:opacity-40 text-white rounded-lg whitespace-nowrap shrink-0"
                  >
                    {t('matching.confirmReversal', 'Storno buchen')}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Interne Umbuchungen (§8.6) */}
      {internalTransfers.length > 0 && (
        <section className="bg-surface border border-hairline rounded-lg p-3 flex items-center justify-between gap-3">
          <p className="text-sm text-secondary">
            {t('matching.transfersHint', '{{count}} interne Umbuchung(en) erkannt', {
              count: internalTransfers.length,
            })}
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={handleMarkTransfers}
            className="text-xs px-3 py-1.5 bg-surface-inset hover:bg-surface-hover disabled:opacity-40 text-secondary rounded-lg whitespace-nowrap"
          >
            {t('matching.markTransfers', '{{count}} Umbuchungen markieren', {
              count: internalTransfers.length,
            })}
          </button>
        </section>
      )}

      {/* Sichere Treffer */}
      {autoSuggestions.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[var(--tone-success-fg)]">
              {t('matching.autoTitle', '✅ Sichere Treffer ({{count}})', { count: autoSuggestions.length })}
            </h3>
            <button
              type="button"
              disabled={busy}
              onClick={handleBulkConfirm}
              className="text-xs px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white rounded-lg"
            >
              {t('matching.bulkConfirm', 'Alle bestätigen')}
            </button>
          </div>
          <div className="space-y-2">{autoSuggestions.map((s) => renderSuggestionRow(s, true))}</div>
        </section>
      )}

      {/* Zu prüfen */}
      {reviewSuggestions.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-secondary mb-2">
            {t('matching.reviewTitle', '🔍 Zu prüfen ({{count}})', { count: reviewSuggestions.length })}
          </h3>
          <div className="space-y-2">{reviewSuggestions.map((s) => renderSuggestionRow(s, false))}</div>
        </section>
      )}

      {/* BR-18-A (§8.11): „Ohne Vorschlag / manuell zuordnen" — Vollständig-
          keits-Sicht für unmatched Umsätze OHNE Auto-/Review-Kandidat.
          Überlappt bewusst mit dem Tab „Ohne Beleg" bei Belastungen (zwei
          Jobs: hier „zuordnen/erledigen", dort „Beleglücke schließen") — der
          Cross-Link unten verweist dorthin. */}
      {unsuggestedTxs.length > 0 && (
        <section>
          <div className="mb-2">
            <h3 className="text-sm font-semibold text-secondary">
              {t('matching.unsuggested.title', '🗂 Ohne Vorschlag / manuell zuordnen ({{count}})', {
                count: unsuggestedTxs.length,
              })}
            </h3>
            <p className="text-xs text-faint mt-0.5">
              {t(
                'matching.unsuggested.subtitle',
                'Diese Umsätze haben keinen automatischen Kandidaten — ordne sie manuell zu oder ignoriere sie.',
              )}
            </p>
            {autoSuggestions.length === 0 &&
              reviewSuggestions.length === 0 &&
              internalTransfers.length === 0 &&
              reversals.length === 0 && (
                <p className="text-xs text-[var(--tone-warning-fg)] mt-1">
                  {t(
                    'matching.empty.noAutoMatches',
                    'Keine automatischen Treffer gefunden — ordne die Umsätze unten manuell zu.',
                  )}
                </p>
              )}
            {onNavigateWithoutReceipt && (
              <button
                type="button"
                onClick={onNavigateWithoutReceipt}
                className="text-xs text-accent-ink hover:text-indigo-200 mt-1"
              >
                {t('matching.unsuggested.toWithoutReceipt', 'Ausgaben ohne Beleg →')}
              </button>
            )}
          </div>
          <div className="space-y-2">{unsuggestedTxs.map((tx) => renderUnsuggestedRow(tx))}</div>
        </section>
      )}

      {splitTx && data && (
        <SplitMatchModal
          tx={splitTx}
          invoices={data.invoices}
          onCancel={() => setSplitTx(null)}
          onConfirm={handleSplitConfirm}
        />
      )}
    </div>
  );
};

export default MatchingPanel;
